import React, { useContext, useEffect, useMemo } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import DropdownSelect from '../../../../../../Common/DropdownSelect';
import { testIds } from '../../../../../../../../util/identifiers/identifiers.util';
import { t } from '../../../../../../../../types/translation/Translator';
import { BinContext } from '../../../../../../../../context/BinContext';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { getQuantityByBinForProduct } from '../../../../../../../../util/product.util';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { BinStatusContext } from '../../../../../../../../context/BinStatusContext';
import { Container } from '../../../../../../../../types/container';
import { Bin } from '../../../../../../../../types/bin';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { Unilever } from '../../../../../../../../types/customer/DHL/Unilever/Unilever';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { HandlingUnitContext } from '../../../../../../../../context/HandlingUnitContext';
import { FlowType } from '../../../../../../../../types/order/orderFlow';
import { System } from '../../../../../../../../types/system';
import { ProductMasterData } from '../../../../../../../../types/product_master_data';
import { OrderFlowContext } from '../../../../../../../../context/OrderFlowContext';

interface ProductTransactionSourceBinProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceBin({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
}: ProductTransactionSourceBinProps) {
  if (transaction.type === ProductTransactionType.inbound) return null;
  if (!productMasterData) return <ContainerTransactionSourceBin container={container} />;

  const { bins } = useContext(BinContext);
  const { orders } = useContext(OrderContext);
  const { products } = useContext(ProductContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { orderFlows } = useContext(OrderFlowContext);

  const selectedHandlingUnit = useMemo(() => {
    return handlingUnits.get(transaction.product.handlingUnitId || '');
  }, [handlingUnits, transaction.product.handlingUnitId]);

  const sourceProductMasterDataId = useMemo(() => {
    return selectedHandlingUnit?.parentId || productMasterData.id;
  }, [selectedHandlingUnit, productMasterData.id]);

  const isDisposal = useMemo(() => {
    const order = transaction.parentId ? orders.get(transaction.parentId) : undefined;
    return order?.type === FlowType.disposal;
  }, [transaction.parentId, orders]);

  const binsForProduct = useMemo(() => {
    const quantityByBin = getQuantityByBinForProduct(
      [...(products.get(sourceProductMasterDataId)?.values() || [])],
      transaction.product.lotId,
    );

    if (!selectedHandlingUnit) return quantityByBin;

    const multiplier = selectedHandlingUnit.quantity;
    const result = new Map<string, number>();
    for (const [binId, quantity] of quantityByBin.entries()) {
      result.set(binId, quantity * multiplier);
    }
    return result;
  }, [products, sourceProductMasterDataId, selectedHandlingUnit, transaction.product.lotId]);

  const allowedBinIds = useMemo(() => {
    const allBinIds = [...binsForProduct.keys()];

    const binIds = new Set();
    for (const binId of allBinIds) {
      const outboundAllowed = binStatuses.get(bins.get(binId)?.binStatusId || '')?.outboundAllowed ?? true;
      if ((isDisposal && !outboundAllowed) || (!isDisposal && outboundAllowed)) {
        binIds.add(binId);
      }
    }
    return binIds;
  }, [binsForProduct, transaction, binStatuses]);

  const flow = orderFlows.get(transaction.flowId);
  if (!flow) return null;

  const current = flow.productTransactionActions.find(action => action.statusId === transaction.statusId);
  if (!current) return null;

  const potentialNextActions = flow.productTransactionActions.filter(action =>
    action.previous.includes(current.statusId),
  );

  const hasDecreaseAction = potentialNextActions.some(
    action => action.productStatusId === System.ProductStatus.decreaseId,
  );

  const sourceBinDisabled = !hasDecreaseAction || disabled || !!transaction.containerTransactionId;

  const validBins = (
    Unilever.companyIds.includes(transaction.companyId)
      ? [...bins.values()].filter(b => b.stockLocationId === transaction.product.fromStockLocationId)
      : [...bins.values()].filter(
          b =>
            b.stockLocationId === transaction.product.fromStockLocationId &&
            binsForProduct.has(b.id) &&
            allowedBinIds.has(b.id),
        )
  ).sort(
    (a, b) =>
      (binsForProduct.get(b.id) || 0) - (binsForProduct.get(a.id) || 0) ||
      parseInt(a.name.match(/\d+/)?.[0] || '0', 10) - parseInt(b.name.match(/\d+/)?.[0] || '0', 10),
  );

  useEffect(() => {
    if (sourceBinDisabled) return;

    if (validBins.length === 1 && !transaction.product.fromBinId) {
      setTransaction(transaction => transaction.withFromBinId(validBins[0].id));
    }
  }, []);

  return (
    <Grid item xs={6}>
      <DropdownSearchSelect<Bin>
        mandatory
        disabled={sourceBinDisabled}
        placeholder={t(TranslationKey.sourceBin).singular.label}
        label={t(TranslationKey.sourceBin).singular.label}
        items={validBins}
        value={bins.get(transaction.product.fromBinId || '')}
        textBuilder={item => item.name}
        itemBuilder={item => (
          <Grid container>
            <Grid item flexGrow={1}>
              <p>{item.name}</p>
            </Grid>
            <Grid item my={'auto'}>
              <p className='text-sm text-gray-400'>{`${UnitOfMeasure.toQuantityWithSuffix(
                productMasterData.unitOfMeasure,
                (binsForProduct.get(item.id) || 0).toString(),
              )}`}</p>
            </Grid>
          </Grid>
        )}
        onSelect={bin => {
          setTransaction(transaction => transaction.withFromBinId(bin?.id).withSerial(undefined).withLPN(undefined));
        }}
      />
    </Grid>
  );
}

interface ContainerTransactionSourceBinProps {
  container: Container | undefined;
}

function ContainerTransactionSourceBin({ container }: ContainerTransactionSourceBinProps) {
  if (!container) return null;

  const { bins } = useContext(BinContext);

  return (
    <Grid item xs={6}>
      <DropdownSelect<Bin>
        disabled
        toText={bin => bin.name}
        onChange={() => {}}
        selectedValue={bins.get(container.binId) || null}
        testId={testIds.bin}
        placeholder={t(TranslationKey.sourceBin).singular.label}
        label={t(TranslationKey.sourceBin).singular.label}
        values={[]}
      />
    </Grid>
  );
}
